// Registers (F01–F05): licences, peer review, CPE, independence, nonattest services, conflicts, security incidents and tools, as
// the engine's register reads publish them (firm_models: readiness, registers_due, licences, …). Due and expiring items are the
// engine's own states (active / expiring / expired / overdue); every record act is a catalogue command through the inbox.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { Card, PageHeader, StatusPill, TabPanel, Tabs } from "../../components";
import { fmtDate } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { ActButton, Computed, ModelGate, Tiles, useActs, useFirmModels } from "./common";

type Doc = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
const KINDS = ["readiness", "registers_due", "licences", "peer_review", "cpe", "independence", "nonattest", "acceptance_register", "security", "tools"] as const;
const TABS = [
  { id: "due", label: "Due" }, { id: "licences", label: "Licences" }, { id: "peer", label: "Peer review" }, { id: "cpe", label: "CPE" },
  { id: "independence", label: "Independence" }, { id: "nonattest", label: "Nonattest" }, { id: "conflicts", label: "Conflicts" },
  { id: "security", label: "Security" }, { id: "tools", label: "Tools" },
];
const prio = (p: string) => (p === "high" ? "blocked" : "waiting");

export function RegistersPage() {
  const ms = useFirmModels([...KINDS]);
  const [tab, setTab] = useState("due");
  const [act, drawer] = useActs();
  const m = ms.data ?? {};
  const due = Array.isArray(m.registers_due?.doc) ? (m.registers_due!.doc as Doc[]) : [];
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Registers" subtitle="Licences, peer review, CPE, independence, nonattest services, conflicts, security and tools — with what is due or expiring." />
      {ms.error && <p role="alert" className="text-sm text-blocked">{ms.error}</p>}
      <ModelGate<Doc> kind="readiness" m={ms.data ? m.readiness : undefined} label="Firm readiness">
        {(r) => (
          <Tiles items={[
            { label: "Firm readiness", value: r.ready ? "Ready" : "Not ready", tone: r.ready ? "done" : "blocked" },
            { label: "Gaps", value: (r.gaps ?? []).length, tone: (r.gaps ?? []).length ? "blocked" : "done" },
            { label: "Due items", value: due.length, tone: due.some((d) => d.priority === "high") ? "blocked" : due.length ? "waiting" : "done" },
            { label: "Peer review due", value: fmtDate(r.sections?.peer_review?.next_due) },
            { label: "SQMS evaluation due", value: fmtDate(r.sections?.sqms1?.next_evaluation_due) },
            { label: "As of", value: fmtDate(r.as_of) },
          ]} />
        )}
      </ModelGate>
      <Tabs tabs={TABS.map((t) => (t.id === "due" ? { ...t, count: due.length } : t))} value={tab} onChange={setTab} label="Registers" idPrefix="reg" />
      {TABS.map((t) => (
        <TabPanel key={t.id} id={t.id} value={tab} idPrefix="reg">
          {!ms.data ? null : t.id === "due" ? (
            <Card title="What is due" action={<Computed m={m.registers_due} />}>
              <ModelGate<Doc[]> kind="registers_due" m={m.registers_due} label="Registers due">
                {(items) => items.length === 0 ? <p className="text-sm text-muted">Nothing is due.</p> : (
                  <ul className="divide-y divide-line">
                    {items.map((d, i) => (
                      <li key={i} className="flex flex-wrap items-start gap-2 py-2 text-sm">
                        <StatusPill state={d.priority === "high" ? "blocked" : "waiting"} kind={prio(d.priority)} label={d.priority === "high" ? "High" : "Due"} />
                        <span className="rounded bg-sunken px-1.5 text-xs text-muted">{d.area}</span><span className="min-w-0 flex-1">{d.what}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </ModelGate>
            </Card>
          ) : t.id === "licences" ? (
            <Card title="Licences and registrations" action={<ActButton command="licence.record" label="Record a licence…" onClick={() => act({ command: "licence.record", title: "Record a licence or registration" })} />}>
              <ModelGate<Doc> kind="licences" m={m.licences}>
                {(d) => (
                  <>
                    <Table rows={d.licences ?? []} empty="No licence recorded." cols={[
                      ["Holder", (l) => l.holder], ["Kind", (l) => String(l.kind).replace(/_/g, " ")], ["Jurisdiction", (l) => l.jurisdiction], ["Number", (l) => <span className="font-mono text-xs">{l.number}</span>],
                      ["Expires", (l) => fmtDate(l.expires_on)], ["State", (l) => <StatusPill state={l.state} />],
                    ]} rowTone={(l) => (l.state === "expired" ? "blocked" : l.state === "expiring" ? "waiting" : undefined)} />
                    {(d.missing ?? []).length > 0 && <p className="mt-2 text-sm text-blocked">Missing: {(d.missing as string[]).join("; ")}</p>}
                    <Sources sources={d.sources} note={`Expiring = within ${d.warning_days ?? "the policy's"} days (FIRM POLICY).`} />
                  </>
                )}
              </ModelGate>
            </Card>
          ) : t.id === "peer" ? (
            <Card title="Peer review" action={<span className="flex flex-wrap gap-2">
              <ActButton command="peerreview.enroll" label="Record enrolment…" onClick={() => act({ command: "peerreview.enroll", title: "Record the peer review enrolment" })} />
              <ActButton command="peerreview.record" label="Record a review…" onClick={() => act({ command: "peerreview.record", title: "Record a peer review" })} />
              <ActButton command="peerreview.finding" label="Record a finding…" onClick={() => act({ command: "peerreview.finding", title: "Record a peer review finding" })} />
            </span>}>
              <ModelGate<Doc> kind="peer_review" m={m.peer_review}>
                {(d) => (
                  <div className="space-y-3 text-sm">
                    <Tiles items={[
                      { label: "Next review due", value: fmtDate(d.next_due), tone: d.overdue ? "blocked" : undefined },
                      { label: "Days to due", value: d.days_to_due ?? "—", tone: d.overdue ? "blocked" : undefined },
                      { label: "Open findings", value: (d.open_findings ?? []).length, tone: (d.open_findings ?? []).length ? "waiting" : "done" },
                      { label: "Last review", value: d.last_review ? `${fmtDate(d.last_review.report_date)} · ${String(d.last_review.rating ?? "").replace(/_/g, " ")}` : "none recorded" },
                    ]} />
                    {d.enrollment && <p>Enrolled with {d.enrollment.administering_entity} on {fmtDate(d.enrollment.enrolled_on)} (no. <span className="font-mono">{d.enrollment.number}</span>).</p>}
                    {(d.open_findings ?? []).map((f: Doc) => (
                      <div key={f.id} className="flex flex-wrap items-center gap-2 rounded border border-line p-2"><StatusPill state="open" /><span className="flex-1">{f.text}</span>
                        <ActButton command="peerreview.remediate" label="Remediate…" onClick={() => act({ command: "peerreview.remediate", fixed: { finding: f.id }, title: `Remediate finding ${f.id}` })} /></div>
                    ))}
                    {(d.issues ?? []).map((x: string) => <p key={x} className="text-blocked">{x}</p>)}
                    <Sources sources={d.sources} />
                  </div>
                )}
              </ModelGate>
            </Card>
          ) : t.id === "cpe" ? (
            <Card title="CPE" action={<span className="flex flex-wrap gap-2">
              <ActButton command="cpe.add" label="Record CPE…" onClick={() => act({ command: "cpe.add", title: "Record completed CPE" })} />
              <ActButton command="cpe.override" label="Override a rule…" onClick={() => act({ command: "cpe.override", title: "Override a CPE rule for a person" })} />
            </span>}>
              <ModelGate<Doc> kind="cpe" m={m.cpe}>
                {(d) => (
                  <ul className="divide-y divide-line text-sm">
                    {(d.people ?? []).map((p: Doc) => (
                      <li key={p.person} className="py-2">
                        <p className="font-medium">{p.person}</p>
                        <div className="mt-1 flex flex-wrap gap-3">
                          {Object.entries((p.compliance ?? {}) as Record<string, Doc>).map(([rule, c]) => (
                            <div key={rule} className="min-w-[14rem] flex-1 rounded border border-line p-2">
                              <span className="flex items-center gap-2 text-xs"><span className="font-semibold">{rule}</span>
                                {c.applies === false ? <StatusPill state="not_required" label="Does not apply" /> : <StatusPill state={c.compliant ? "done" : "missing"} label={c.compliant ? "Compliant" : "Short"} />}</span>
                              {c.applies === false ? <p className="text-xs text-muted">{c.why}</p> : (c.shortfalls ?? []).map((s: string) => <p key={s} className="text-xs text-blocked">{s}</p>)}
                            </div>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </ModelGate>
            </Card>
          ) : t.id === "independence" ? (
            <Card title="Independence" action={<ActButton command="independence.confirm" label="Confirm my independence…" onClick={() => act({ command: "independence.confirm", initial: { scope: "annual" }, title: "Confirm your independence" })} />}>
              <ModelGate<Doc> kind="independence" m={m.independence}>
                {(d) => (
                  <div className="space-y-2 text-sm">
                    <p className="text-muted">Annual confirmations for {d.year} (FIRM POLICY: annual).</p>
                    <ul className="divide-y divide-line">
                      {(d.annual ?? []).map((a: Doc) => <li key={a.person} className="flex items-center gap-2 py-1.5"><StatusPill state="done" label="Confirmed" /><span className="flex-1">{a.person}</span><span className="text-xs text-muted">signed {fmtDate(a.signed_on)}{a.threats ? ` · ${a.threats}` : ""}</span></li>)}
                      {(d.annual_missing ?? []).map((p: string) => <li key={p} className="flex items-center gap-2 py-1.5"><StatusPill state="missing" label="Not confirmed" /><span className="flex-1">{p}</span></li>)}
                    </ul>
                  </div>
                )}
              </ModelGate>
            </Card>
          ) : t.id === "nonattest" ? (
            <Card title="Nonattest services" action={<ActButton command="nonattest.add" label="Record a service…" onClick={() => act({ command: "nonattest.add", title: "Record a nonattest service" })} />}>
              <ModelGate<Doc[]> kind="nonattest" m={m.nonattest} label="Nonattest services">
                {(rows) => <Table rows={rows} empty="No nonattest service recorded." cols={[
                  ["Client", (r) => r.client], ["Service", (r) => r.service], ["Oversight (SKE)", (r) => r.ske_person], ["Understanding", (r) => fmtDate(r.understanding_on)],
                  ["Conclusion", (r) => <StatusPill state={r.conclusion === "threats_at_acceptable_level" ? "done" : "blocked"} label={String(r.conclusion).replace(/_/g, " ")} />],
                  ["Safeguards", (r) => <span className="text-xs">{r.safeguards}</span>],
                ]} rowTone={(r) => (r.conclusion === "independence_impaired" ? "blocked" : undefined)} />}
              </ModelGate>
            </Card>
          ) : t.id === "conflicts" ? (
            <Card title="Acceptance and conflicts" action={<span className="flex flex-wrap gap-2">
              <ActButton command="conflict.check" label="Run a conflicts check…" onClick={() => act({ command: "conflict.check", title: "Run a conflicts check" })} />
              <ActButton command="conflict.conclude" label="Conclude a check…" onClick={() => act({ command: "conflict.conclude", title: "Conclude a conflicts check" })} />
            </span>}>
              <ModelGate<Doc[]> kind="acceptance_register" m={m.acceptance_register} label="Acceptance register">
                {(rows) => <Engagements rows={rows} />}
              </ModelGate>
            </Card>
          ) : t.id === "security" ? (
            <Card title="Information security" action={<span className="flex flex-wrap gap-2">
              <ActButton command="incident.log" label="Log an incident…" onClick={() => act({ command: "incident.log", title: "Log a security incident" })} />
              <ActButton command="wisp.record" label="Record the WISP…" onClick={() => act({ command: "wisp.record", title: "Record the written information security program" })} />
            </span>}>
              <ModelGate<Doc> kind="security" m={m.security}>
                {(d) => (
                  <div className="space-y-3 text-sm">
                    <p className="flex flex-wrap items-center gap-2">Written information security program:
                      {d.wisp ? <><StatusPill state={d.review_overdue ? "expired" : "active"} label={d.review_overdue ? "Review overdue" : "Recorded"} /><span className="text-muted">next review {fmtDate(d.wisp.next_review)}</span></> : <StatusPill state="missing" label="Not recorded" />}</p>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Open incidents</h3>
                    {(d.open_incidents ?? []).length === 0 ? <p className="text-muted">No open incident.</p> : (
                      <ul className="divide-y divide-line">
                        {(d.open_incidents as Doc[]).map((i) => (
                          <li key={i.id} className="flex flex-wrap items-center gap-2 py-2"><StatusPill state="blocked" label="Awaits notification decision" /><span className="min-w-0 flex-1">#{i.id} · discovered {fmtDate(i.discovered_on)} — {i.description}</span>
                            <ActButton command="incident.close" label="Close…" onClick={() => act({ command: "incident.close", fixed: { incident: i.id }, title: `Close incident ${i.id}` })} /></li>
                        ))}
                      </ul>
                    )}
                    <Sources sources={d.sources} />
                  </div>
                )}
              </ModelGate>
            </Card>
          ) : (
            <Card title="Tools register" action={<ActButton command="tool.register" label="Register a tool…" onClick={() => act({ command: "tool.register", title: "Register a tool" })} />}>
              <ModelGate<Doc[]> kind="tools" m={m.tools} label="Tools">
                {(rows) => <Table rows={rows} empty="No tool registered." cols={[
                  ["Tool", (r) => <span>{r.tool} <span className="text-xs text-muted">{r.version}</span><span className="block text-xs text-muted">{r.purpose}</span></span>],
                  ["Approved", (r) => fmtDate(r.approved_on)], ["Next review", (r) => fmtDate(r.next_review)],
                  ["State", (r) => <StatusPill state={r.retired_at ? "ended" : r.review_overdue ? "expired" : "active"} label={r.retired_at ? "Retired" : r.review_overdue ? "Review overdue" : "Approved"} />],
                  ["", (r) => (r.retired_at ? null : <ActButton command="tool.retire" label="Retire…" onClick={() => act({ command: "tool.retire", fixed: { id: r.id }, title: `Retire ${r.tool}` })} />)],
                ]} rowTone={(r) => (r.review_overdue && !r.retired_at ? "blocked" : undefined)} />}
              </ModelGate>
            </Card>
          )}
        </TabPanel>
      ))}
      {drawer}
    </div>
  );
}

function Engagements({ rows }: { rows: Doc[] }) {
  const { ds } = useApp();
  const mine = useLoad(async () => new Set((await ds.engagements()).map((e) => e.id)), [ds]);
  return <Table rows={rows} empty="No engagement accepted yet." cols={[
    ["Client", (r) => (mine.data?.has(r.engagement) ? <Link className="text-brand hover:underline" to={`/e/${r.engagement}`}>{r.client}</Link> : r.client)],
    ["Year end", (r) => fmtDate(r.fye)], ["Kind", (r) => r.kind], ["Accepted", (r) => (r.accepted ? `${r.accepted.by} · ${fmtDate(r.accepted.at)}` : <StatusPill state="missing" label="Not yet" />)],
    ["Independence", (r) => <StatusPill state={r.independence?.attested ? "done" : "waiting"} label={`${r.independence?.confirmed ?? 0}/${r.independence?.team ?? 0} confirmed`} />],
    ["Conflicts check", (r) => <StatusPill state={r.conflict_check === "none recorded" ? "missing" : "done"} label={String(r.conflict_check)} />],
    ["Nonattest", (r) => r.nonattest_services],
  ]} />;
}

export function Table({ rows, cols, empty, rowTone }: { rows: Doc[]; cols: Array<[string, (r: Doc) => React.ReactNode]>; empty: string; rowTone?: (r: Doc) => "blocked" | "waiting" | undefined }) {
  if (!rows.length) return <p className="text-sm text-muted">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-line text-left text-xs text-muted">{cols.map(([h], i) => <th key={i} scope="col" className="px-2 py-1.5 font-medium">{h || <span className="sr-only">Act</span>}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const tone = rowTone?.(r);
            return <tr key={i} className={`border-b border-line last:border-0 ${tone === "blocked" ? "bg-blocked-bg" : tone === "waiting" ? "bg-waiting-bg" : ""}`}>{cols.map(([, f], j) => <td key={j} className="px-2 py-1.5 align-top">{f(r)}</td>)}</tr>;
          })}
        </tbody>
      </table>
    </div>
  );
}

function Sources({ sources, note }: { sources?: Doc[]; note?: string }) {
  if (!sources?.length && !note) return null;
  return (
    <details className="mt-3 text-xs text-muted">
      <summary className="cursor-pointer">Sources{note ? ` · ${note}` : ""}</summary>
      <ul className="mt-1 list-inside list-disc space-y-0.5">
        {(sources ?? []).map((s) => <li key={s.cite}>{s.url ? <a className="text-brand hover:underline" href={s.url} target="_blank" rel="noreferrer">{s.cite}</a> : s.cite}{s.verified ? "" : " — not yet verified against the primary text"}</li>)}
      </ul>
    </details>
  );
}
